const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/triemli/eCheckin/sections/Wohnsitz.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { Alert } from 'App/components/Alert/Alert';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import _ from 'lodash';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';

const getAddressPaths = (prefix?: string) => {
    const addPrefix = (path: string) => {
        const newPath = `${prefix ?? ''}${path}`;
        return `${newPath[0].toLowerCase()}${newPath.slice(1)}`;
    };

    return {
        COUNTRY: addPrefix('Country'),
        CO: addPrefix('Co'),
        STREET_AND_NUMBER: addPrefix('StreetAndNumber'),
        PLZ: addPrefix('Plz'),
        ORT: addPrefix('Ort'),
        KANTON: addPrefix('Kanton'),
    };
};

type AddressPaths = ReturnType<typeof getAddressPaths>;

const getAddressConfig = (paths: AddressPaths): AddressFieldsConfig => ({
    streetAndNumber: { path: paths.STREET_AND_NUMBER, required: true },
    additionalInfo: { included: false },
    postalCode: { path: paths.PLZ, required: true },
    location: { path: paths.ORT, required: true },
    country: { path: paths.COUNTRY, required: true },
});

export const Wohnsitz = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const wohnsitzHinweis = useText(textMap.wohnsitzHinweis);

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const PATH_ADDRESS = getAddressPaths();
    const PATH_CORRESPONDENCE = getAddressPaths('korresp');
    const PATH_CORRESPONDENCE_ADDRESS = createFieldPath(path, 'korrespAddress');

    const addressConfig = getAddressConfig(PATH_ADDRESS);
    const correspondenceAddressConfig = getAddressConfig(PATH_CORRESPONDENCE);

    const isNotCorrespondenceAddress = _.get(formData, PATH_CORRESPONDENCE_ADDRESS) === '1';
    const isNotSwissAddress = _.get(formData, createFieldPath(path, PATH_ADDRESS.COUNTRY)) !== 'CH';
    const isNotSwissCorrespondence = _.get(formData, createFieldPath(path, PATH_CORRESPONDENCE.COUNTRY)) !== 'CH';

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <Alert color={'info'} hideIcon>
                {wohnsitzHinweis}
            </Alert>
            <AddressBlock basePath={path} fieldsConfig={addressConfig} required />
            <CIS4mTextField
                label={textMap.kanton}
                path={createFieldPath(path, PATH_ADDRESS.KANTON)}
                required
                hidden={isNotSwissAddress}
            />
            <YesNoField label={textMap.correspondenceAddress} path={PATH_CORRESPONDENCE_ADDRESS} />
            <CIS4mBlock hidden={!isNotCorrespondenceAddress}>
                <CIS4mTextField label={textMap.co} path={createFieldPath(path, PATH_CORRESPONDENCE.CO)} />
                <AddressBlock basePath={path} fieldsConfig={correspondenceAddressConfig} required />
                <CIS4mTextField
                    label={textMap.kanton}
                    path={createFieldPath(path, PATH_CORRESPONDENCE.KANTON)}
                    required
                    hidden={isNotSwissCorrespondence}
                />
            </CIS4mBlock>
        </ECheckinSection>
    );
});

Wohnsitz.displayName = 'Wohnsitz';

export default Wohnsitz;
